import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Repeat, AlertTriangle, Check, X } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useApi } from '@/lib/useApi';
import { useAuth } from '@/lib/auth';
import { money, date, humanise } from '@/lib/format';
import {
  Badge, Button, Card, CardHeader, EmptyState, Field, LoadingBlock, Modal,
  Textarea, useToast,
} from './ui';
import type { PaymentSchedule } from '@shared/types';

/**
 * What tenants say they have set up with their banks.
 *
 * ── What confirming one does ──────────────────────────────────────────────
 *
 * It moves that tenancy off "claim each payment with proof" and onto the
 * standing-order reconciliation list. It does not mark any rent paid, ever: a
 * standing order is an instruction to a bank, and only a statement shows the
 * money moved. Saying that on the screen is cheaper than the support message
 * asking why the balance did not change.
 */
export function PaymentSchedules() {
  const { currency, can } = useAuth();
  const toast = useToast();
  const [declining, setDeclining] = useState<PaymentSchedule | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const { data, loading, reload } = useApi<{
    schedules: PaymentSchedule[];
    awaiting_review: number;
  }>('/payment-schedules');

  const all = data?.schedules ?? [];
  const rows = showAll ? all : all.filter((s) => s.status === 'declared' || s.status === 'confirmed');

  async function review(schedule: PaymentSchedule, decision: 'confirm' | 'decline', note?: string) {
    setBusy(true);
    try {
      await api.post(`/payment-schedules/${schedule.id}/review`, {
        decision, review_note: note,
      });
      toast.success(decision === 'confirm'
        ? 'Confirmed. Their rent moves to the standing-order list.'
        : 'Declined, and they have been told why.');
      setDeclining(null);
      setReason('');
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'That could not be saved');
    } finally {
      setBusy(false);
    }
  }

  const cycleWord = (c: string) => (c === 'weekly' ? 'week'
    : c === 'quarterly' ? 'quarter' : c === 'yearly' ? 'year' : 'month');

  if (loading && !data) return <LoadingBlock rows={4} />;

  if (rows.length === 0) {
    return (
      <EmptyState
        icon={<Repeat className="h-6 w-6" />}
        title="No standing orders on file"
        message="When a tenant tells you they have set one up, it appears here for you to confirm. You can also record one yourself from their tenancy."
        action={
          all.length > 0 ? <Button onClick={() => setShowAll(true)}>Show past ones</Button> : undefined
        }
      />
    );
  }

  return (
    <div className="space-y-4">
      <Card className="p-0 overflow-hidden">
        <CardHeader
          title="Standing orders"
          subtitle={data && data.awaiting_review > 0
            ? `${data.awaiting_review} waiting for you`
            : 'Nothing waiting on you'}
          action={
            <Button size="sm" onClick={() => setShowAll((v) => !v)}>
              {showAll ? 'Hide past ones' : 'Show past ones'}
            </Button>
          }
        />
        <ul className="divide-y divide-line">
          {rows.map((s) => (
            <li key={s.id} className="p-4 sm:p-5 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={
                  s.status === 'confirmed' ? 'ok'
                    : s.status === 'declared' ? 'info'
                    : 'neutral'
                }>
                  {s.status === 'declared' ? 'Waiting for you' : humanise(s.status)}
                </Badge>
                {s.tenant_id ? (
                  <Link to={`/app/tenants/${s.tenant_id}`} className="font-medium hover:text-brand">
                    {s.tenant_name}
                  </Link>
                ) : <span className="font-medium">{s.tenant_name}</span>}
                <span className="text-sm text-muted">
                  {money(s.amount_cents, currency)} every {cycleWord(s.cycle)}
                  {s.first_payment_date ? `, from ${date(s.first_payment_date)}` : ''}
                </span>
                <span className="text-xs text-faint ml-auto">
                  {s.property_name} · {s.source === 'tenant' ? 'they told us' : 'recorded by us'}
                </span>
              </div>

              {s.reference && (
                <p className="text-xs text-muted">
                  Quoting <span className="font-mono text-ink">{s.reference}</span>
                  {' — this is what an imported statement matches on'}
                </p>
              )}
              {s.note && <p className="text-sm text-muted">&ldquo;{s.note}&rdquo;</p>}

              {/*
                A difference worth having an opinion about, not the rounding
                between 52 weeks and 12 months — the backend applies a
                tolerance before anything gets here.
              */}
              {s.mismatch && (
                <div className="flex items-start gap-2.5 p-3 rounded-xl bg-warn/10 border border-warn/25">
                  <AlertTriangle className="h-4 w-4 text-warn shrink-0 mt-0.5" />
                  <p className="text-sm leading-relaxed">
                    This comes to {money(s.mismatch.declared_per_year_cents, currency)} a year
                    against rent of {money(s.mismatch.expected_per_year_cents, currency)}.
                    {s.mismatch.short ? ' They would fall behind by ' : ' They would overpay by '}
                    {money(Math.abs(s.mismatch.difference_per_year_cents), currency)} a year.
                  </p>
                </div>
              )}

              {s.review_note && (
                <p className="text-sm border-l-2 border-line pl-3 text-muted">{s.review_note}</p>
              )}

              {s.status === 'declared' && can('manager') && (
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button
                    size="sm"
                    variant="primary"
                    icon={<Check className="h-3.5 w-3.5" />}
                    loading={busy}
                    onClick={() => void review(s, 'confirm')}
                  >
                    Confirm
                  </Button>
                  <Button
                    size="sm"
                    icon={<X className="h-3.5 w-3.5" />}
                    onClick={() => { setDeclining(s); setReason(''); }}
                  >
                    Decline
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      </Card>

      <p className="text-xs text-faint leading-relaxed px-1">
        Confirming one moves that tenant onto the standing-order list — it never marks
        rent as paid. A standing order tells a bank to pay; it can be cancelled and it
        can bounce, so what arrived is still confirmed from your statement.
      </p>

      <Modal
        open={Boolean(declining)}
        onClose={() => setDeclining(null)}
        title="Decline this standing order"
        subtitle="They are told what you say here, so make it something they can act on."
        footer={(
          <>
            <Button onClick={() => setDeclining(null)}>Cancel</Button>
            <Button
              variant="primary"
              loading={busy}
              disabled={reason.trim().length === 0}
              onClick={() => declining && void review(declining, 'decline', reason.trim())}
            >
              Decline and tell them
            </Button>
          </>
        )}
      >
        <Field label="Why?" required>
          <Textarea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="The amount is short by £50 — please check it with your bank."
            autoFocus
          />
        </Field>
      </Modal>
    </div>
  );
}
